import React from 'react';
import {WithPermission} from 'admin-on-rest';
import {resolveAccess} from '../shared/authFunctions';
import {ViewTitle} from 'admin-on-rest/lib/mui';
import {Card, CardHeader, CardText, CardActions} from 'material-ui/Card';
import FlatButton from 'material-ui/FlatButton';
import Avatar from 'material-ui/Avatar';
import ListIcon from 'material-ui/svg-icons/action/view-list';
import AddIcon from 'material-ui/svg-icons/content/add';
import {fuseProductIcon as ProductIcon} from './product';
import {fuseProductTypeIcon as ProductTypeIcon} from './productType';
import {fuseProductCategoryIcon as ProductCategoryIcon} from './category';
import {fuseProductAliasIcon as ProductAliasIcon} from './alias';
import {fuseDiscountIcon as DiscountIcon} from './discount';
import {dashStyles} from '../shared/styles-dashboard';

export default () => (
    <div>
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np'], [])}>
            <Card style={{...dashStyles.flexRow, ...dashStyles.marginBottom}}>
                <ProductIcon style={dashStyles.dashIcon}/>
                <ViewTitle title="Products"/>
                <CardText>
                    <img src="img-diagram-Products-Dashboard.png" alt="Diagram of Products Tree"
                         style={dashStyles.diagramLeft}/>
                    <p>
                        Create and Manage all of your Products for your Business.</p>
                    <p>
                        Here you can Create, View and Edit <em>Product Types</em>, <em>Product Categories</em>,&nbsp;
                        <em>Products</em>, <em>Product Units</em>, <em>Stock Keeping Units (SKUs)</em>,
                        and <em>Discounts</em>.
                    </p>
                    <hr style={dashStyles.break}/>
                </CardText>
            </Card>
        </WithPermission>

        {/* Products View Only */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['products_vo'])}>
            <Card style={{...dashStyles.marginBottom}}>
                <ProductIcon style={dashStyles.dashIcon}/>
                <ViewTitle title="Products"/>
                <CardText>
                    <img src="img-diagram-Products-Dashboard.png" alt="Diagram of Products Tree"
                         style={dashStyles.diagramLeft}/>
                    <p>
                        View all of your Products for your Business.</p>
                    <p>
                        Here you can View <em>Product Types</em>, <em>Product Categories</em>,&nbsp;
                        <em>Products</em>, <em>Product Units</em>, <em>Stock Keeping Units (SKUs)</em>,
                        and <em>Discounts</em>.
                    </p>
                    <hr style={dashStyles.break}/>
                </CardText>
            </Card>
        </WithPermission>

        {/* Product Types */}
        <WithPermission
            resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np', 'products_vo'], [])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}}>
                <CardHeader
                    title="Product Types"
                    subtitle="Categorical or Trade name associated with Products. Click to read more."
                    avatar={<Avatar icon={<ProductTypeIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <img src="img-diagram-Product-Type-List.png" alt="Diagram of Products - Product Types"
                         style={dashStyles.diagramSmall}/>
                    <p>
                        A <strong>Product Type</strong> defines the kind of products to be sold in your store(s). Most
                        businesses have only 1 Product Type.
                    </p>
                    <p>
                        There are instances in the Seasonal Retail Industry, where you might have different Product
                        Types
                        depending on the Season. For example, you could have a Store that sells Fireworks in the Summer,
                        then sells Halloween Costumes in October. Instead of managing all those Products under one
                        Product
                        Type, you can create multiple Product Types to manage those Products.
                    </p>
                    <p>
                        Also, when creating a DBA, you select a Product Type. Then when creating a Location, you’ll
                        select
                        the DBA for that location. This configures a Location's POS System to display only products of
                        the selected Product Type.
                    </p>
                    <p style={dashStyles.detail}>
                        For Example, Location X's DBA has a Product Type of <em>Fireworks</em>. That POS System will
                        only be
                        able to sell Fireworks Products. Location Y's DBA has a Product Type of <em>Halloween</em>, so
                        that
                        POS System will only be able to sell Halloween Products. It won't have access to Location X's
                        Products.
                    </p>
                    <hr style={dashStyles.break}/>
                </CardText>
                {/* Buttons For Product Types Full Access */}
                <WithPermission
                    resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Product Types" href="#/ProductTypes"/>
                        <FlatButton icon={<AddIcon/>} label="Add a Product Type" href="#/ProductTypes/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons For Product Types View Only Access */}
                <WithPermission
                    resolve={(props) => resolveAccess(props.permissions, [], ['products_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Product Types" href="#/ProductTypes"/>
                    </CardActions>
                </WithPermission>
            </Card>
        </WithPermission>

        {/* Categories */}
        <WithPermission
            resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np', 'products_vo'], [])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}}>
                <CardHeader
                    title="Product Categories"
                    subtitle="Top-level and child Categories of a Product Type. Click to read more."
                    avatar={<Avatar icon={<ProductCategoryIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <img src="img-diagram-Product-Category-List.png" alt="Diagram of Products - Product Categories"
                         style={dashStyles.diagramSmall}/>
                    <p>
                        <strong>Product Categories</strong> group related Products on the POS, so employees can navigate
                                                            to the Product they need. A Product Category will have a
                                                            list of Products related in a specific
                                                            way.
                    </p>
                    <p>
                        For example, A Product Category named <em>Food</em> will have all products that are food
                        related.
                        Another Product Category named <em>Toys</em> will have all products that are toy related.
                    </p>
                    <p>
                        Product Categories can also have sub categories. For example, Product
                        Category <em>Food</em> might
                        have
                        subcategories <em>Fruit</em>, <em>Vegetables</em>, <em>Meat</em>, etc. In those subcategories
                        are
                        Products related to the subcategory.
                    </p>
                    <hr style={dashStyles.break}/>
                </CardText>
                {/* Buttons For Categories Full Access */}
                <WithPermission
                    resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np'], [])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Product Categories" href="#/ProductCategories"/>
                        <FlatButton icon={<AddIcon/>} label="Add a Product Category" href="#/ProductCategories/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons For Categories View Only Access */}
                <WithPermission
                    resolve={(props) => resolveAccess(props.permissions, [], ['products_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Product Categories" href="#/ProductCategories"/>
                    </CardActions>
                </WithPermission>
            </Card>
        </WithPermission>

        {/* Products */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np'], [])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}}>
                <CardHeader
                    title="Products and Product Units"
                    subtitle="Product details, brands and categories, and sellable Product Units. Click to read more."
                    actAsExpander={true}
                    showExpandableButton={true}
                    avatar={<Avatar icon={<ProductIcon/>} style={dashStyles.avatar}/>}
                />
                <CardText expandable={true}>
                    <img src="img-diagram-Product-List.png" alt="Diagram of Products - Products"
                         style={dashStyles.diagramSmall}/>
                    <p>
                        A <strong>Product</strong> provides essential information about Product Units. A Product is not
                        a
                        physical sellable unit, but a parent to a Product Unit.
                    </p>
                    <p>
                        A Product's Product Units are sellable items (single or multiple), and the Product helps provide
                        different Product Units with the same essential information such as Type, Brand name, default
                        quantities, and Category.
                    </p>
                    <hr style={dashStyles.break}/>
                    <hr/>
                    <br/>
                    <img src="img-diagram-Product-Unit-List.png" alt="Diagram of Products - Product Units"
                         style={dashStyles.diagramSmall}/>
                    <p>
                        A <strong>Product Unit</strong> is a physical item or set that is sold at your Location(s), and
                        contains important information such as barcode and prices. A Product Unit can be a single
                        sellable item (called a <em>Piece</em>) or multiple pieces bundled
                        together (<em>Mid</em>, <em>Case</em>) to be sold.</p>
                    <p>
                        Product Units have a barcode, a Unit Type, and a default price. Additional prices may be added
                        for specific Locations (called Location Prices), or for specific Price Levels (called Price
                        Level Prices).
                    </p>
                    <hr style={dashStyles.break}/>
                    <p>
                        After you create a Product, you may create one or more Product Units for that Product.
                    </p>
                </CardText>
                <CardActions>
                    <FlatButton icon={<ListIcon/>} label="List Products" href="#/Products"/>
                    <FlatButton icon={<AddIcon/>} label="Add a Product" href="#/Products/create"/>
                </CardActions>
            </Card>
        </WithPermission>

        {/* Products View Only */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, [], ['products_vo'])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}}>
                <CardHeader
                    title="Products and Product Units"
                    subtitle="Product details, brands and categories, and sellable Product Units. Click to read more."
                    actAsExpander={true}
                    showExpandableButton={true}
                    avatar={<Avatar icon={<ProductIcon/>} style={dashStyles.avatar}/>}
                />
                <CardText expandable={true}>
                    <img src="img-diagram-Product-List.png" alt="Diagram of Products - Products"
                         style={dashStyles.diagramSmall}/>
                    <p>
                        A <strong>Product</strong> provides essential information about Product Units. A Product is not
                        a
                        physical sellable unit, but a parent to a Product Unit.
                    </p>
                    <p>
                        A Product's Product Units are sellable items (single or multiple), and the Product helps provide
                        different Product Units with the same essential information such as Type, Brand name, default
                        quantities, and Category.
                    </p>
                    <hr style={dashStyles.break}/>
                    <hr/>
                    <br/>
                    <img src="img-diagram-Product-Unit-List.png" alt="Diagram of Products - Product Units"
                         style={dashStyles.diagramSmall}/>
                    <p>
                        A <strong>Product Unit</strong> is a physical item or set that is sold at your Location(s), and
                        contains important information such as barcode and prices. A Product Unit can be a single
                        sellable item (called a <em>Piece</em>) or multiple pieces bundled
                        together (<em>Mid</em>, <em>Case</em>) to be sold.</p>
                    <p>
                        Product Units have a barcode, a Unit Type, and a default price. Additional prices may be added
                        for specific Locations (called Location Prices), or for specific Price Levels (called Price
                        Level Prices).
                    </p>
                    <hr style={dashStyles.break}/>
                </CardText>
                <CardActions>
                    <FlatButton icon={<ListIcon/>} label="List Products" href="#/Products"/>
                </CardActions>
            </Card>
        </WithPermission>

        {/* SKUs / Aliases */}
        <WithPermission
            resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np', 'products_vo'], [])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}}>
                <CardHeader
                    title="Product SKUs"
                    subtitle="Alternate barcodes for specific Product Units. Click to read more."
                    avatar={<Avatar icon={<ProductAliasIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <img src="img-diagram-Product-Sku-List.png" alt="Diagram of Products - SKUs"
                         style={dashStyles.diagramSmall}/>
                    <p>
                        Product <strong>SKUs</strong> are created to identify certain Product Units by a different
                        barcode
                        number. All Product Units have a barcode, but if a Product Unit has another barcode associated
                        with it, create a SKU by selecting a Product Unit and entering the barcode number.
                    </p>
                    <hr style={dashStyles.break}/>
                </CardText>
                {/* Buttons Full Access */}
                <WithPermission
                    resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np'], [])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List SKUs for all Products" href="#/ProductAliases"/>
                        <FlatButton icon={<AddIcon/>} label="Add a SKU to a Product" href="#/ProductAliases/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons View Only Access */}
                <WithPermission
                    resolve={(props) => resolveAccess(props.permissions, [], ['products_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List SKUs for all Products" href="#/ProductAliases"/>
                    </CardActions>
                </WithPermission>
            </Card>
        </WithPermission>

        {/* Discounts */}
        <WithPermission resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np', 'products_vo'], [])}>
            <Card style={{...dashStyles.listCard, ...dashStyles.marginBottom}}>
                <CardHeader
                    title="Product Discounts"
                    subtitle="Coupon and Discount functionality for specific Product Units and Categories. Click to read more."
                    avatar={<Avatar icon={<DiscountIcon/>} style={dashStyles.avatar}/>}
                    actAsExpander={true}
                    showExpandableButton={true}
                />
                <CardText expandable={true}>
                    <img src="img-diagram-Discount-List.png" alt="Diagram of Products - Discounts"
                         style={{...dashStyles.diagramSmall, ...dashStyles.diagramLeft}}/>
                    <p>
                        Product <strong>Discounts</strong> can be used to create discounts and coupons for specific
                        Product
                        Units, multiple Product Units, or Categories.
                    </p>
                    <hr style={dashStyles.break}/>
                </CardText>
                {/* Buttons Full Access */}
                <WithPermission
                    resolve={(props) => resolveAccess(props.permissions, ['products_all', 'products_np'], [])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Discounts" href="#/Discounts"/>
                        <FlatButton icon={<AddIcon/>} label="Add a Discount" href="#/Discounts/create"/>
                    </CardActions>
                </WithPermission>
                {/* Buttons View Only Access */}
                <WithPermission
                    resolve={(props) => resolveAccess(props.permissions, [], ['products_vo'])}>
                    <CardActions>
                        <FlatButton icon={<ListIcon/>} label="List Discounts" href="#/Discounts"/>
                    </CardActions>
                </WithPermission>
            </Card>
        </WithPermission>
    </div>
);



// WEBPACK FOOTER //
// ./src/Fuse/Product/dashboard.js